import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { environment } from '@/config/environment';

// Website API key (must match backend WEBSITE_API_KEY). Required when backend has client key auth.
const apiKey = (import.meta.env.VITE_WEBSITE_API_KEY || import.meta.env.VITE_API_KEY || "").trim();

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: environment.admin,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    ...(apiKey ? { 'x-api-key': apiKey } : {}),
  },
});

// Request interceptor - Add auth token and ensure x-api-key is sent
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Let the runtime set multipart boundary; default JSON Content-Type breaks file uploads (Multer sees no file → 400).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }
    if (apiKey) {
      config.headers.set('x-api-key', apiKey);
    }
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and token expiry
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { status } = error.response;

      // Handle 401 Unauthorized - Token expired or invalid
      if (status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        // Redirect to login
        if (window.location.pathname !== '/sign-in') {
          window.location.href = '/sign-in';
        }
      }

      // Handle 403 Forbidden
      if (status === 403) {
        console.error('Access forbidden');
      }

      // Handle 500 Server Error
      if (status >= 500) {
        console.error('Server error:', error.response.data);
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient };
export default apiClient;
